import { useMemo } from "react";
import type { ViewTableLoadMore } from "@design-system/ViewTable/ViewTableLoadMore";

/** `ViewTable` load-more props without the error copy, which the table names. */
export type LoadMoreTableState = Omit<ViewTableLoadMore, "errorLabel">;

/** What a load-more list hook reports about its next batch. */
interface LoadMoreProgress {
    hasMore: boolean;
    isLoadingMore: boolean;
    isLoadMoreError: boolean;
    loadNextPage: () => void;
    retry: () => void;
    resetKey: string;
}

/** A load-more list hook's progress as stable `ViewTable` props. */
export function useLoadMoreTableState({
    hasMore,
    isLoadingMore,
    isLoadMoreError,
    loadNextPage,
    retry,
    resetKey,
}: LoadMoreProgress): LoadMoreTableState {
    return useMemo(
        () => ({
            hasMore,
            isLoading: isLoadingMore,
            isError: isLoadMoreError,
            onLoadMore: loadNextPage,
            onRetry: retry,
            resetKey,
        }),
        [
            hasMore,
            isLoadingMore,
            isLoadMoreError,
            loadNextPage,
            retry,
            resetKey,
        ],
    );
}

/** Adds the table's error copy to a load-more state; `undefined` while paging. */
export function useViewTableLoadMore(
    loadMore: LoadMoreTableState | undefined,
    errorLabel: string,
): ViewTableLoadMore | undefined {
    return useMemo(
        () => loadMore && { ...loadMore, errorLabel },
        [loadMore, errorLabel],
    );
}
